import React, { useState } from 'react';
import { User, Phone, Mail, MessageSquare, Check, Loader2, X } from 'lucide-react';

const ContactFormPostbariatrica = ({ onSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    mensaje: '',
    peso_perdido: '',
    tiempo_estabilizado: '',
    procedimientos_interes: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const procedimientosOpciones = [
    { id: 'dermolipectomia', label: 'Dermolipectomía abdominal' },
    { id: 'bodylift', label: 'Body lift inferior' },
    { id: 'braquioplastia', label: 'Braquioplastia (brazos)' },
    { id: 'lifting_muslos', label: 'Lifting de muslos' },
    { id: 'mastopexia', label: 'Mastopexia / Aumento mamario' },
    { id: 'lifting_facial', label: 'Lifting facial y cervical' },
    { id: 'no_seguro', label: 'No estoy seguro/a' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProcedimientoToggle = (procId) => {
    setFormData(prev => {
      const current = prev.procedimientos_interes;
      if (current.includes(procId)) {
        return { ...prev, procedimientos_interes: current.filter(p => p !== procId) };
      } else {
        return { ...prev, procedimientos_interes: [...current, procId] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      let mensajeCompleto = formData.mensaje || '';
      if (formData.peso_perdido) {
        mensajeCompleto += `\n\nPeso perdido aproximado: ${formData.peso_perdido} kg`;
      }
      if (formData.tiempo_estabilizado) {
        mensajeCompleto += `\nTiempo con peso estabilizado: ${formData.tiempo_estabilizado}`;
      }
      if (formData.procedimientos_interes.length > 0) {
        const procsSeleccionados = formData.procedimientos_interes.map(id => {
          const proc = procedimientosOpciones.find(p => p.id === id);
          return proc ? proc.label : id;
        });
        mensajeCompleto += `\nProcedimientos de interés: ${procsSeleccionados.join(', ')}`;
      }

      const submitData = {
        nombre: formData.nombre,
        telefono: formData.telefono,
        email: formData.email,
        mensaje: mensajeCompleto.trim(),
        servicio: 'postbariatrica'
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacto/postbariatrica`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          telefono: '',
          email: '',
          mensaje: '',
          peso_perdido: '',
          tiempo_estabilizado: '',
          procedimientos_interes: []
        });
        if (onSuccess) onSuccess();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="bg-ivory p-10 max-w-2xl mx-auto">
        <div className="text-center py-8">
          <div className="w-16 h-16 border border-gold flex items-center justify-center mx-auto mb-8">
            <Check className="text-gold" size={32} />
          </div>
          <h3 className="font-serif text-2xl text-charcoal mb-4">
            Solicitud enviada correctamente
          </h3>
          <p className="text-text-secondary mb-8">
            Gracias por contactar con nosotros. El equipo del Dr. Norro revisará su solicitud 
            y se pondrá en contacto con usted a la mayor brevedad posible.
          </p>
          <button 
            onClick={() => {
              setSubmitStatus(null);
              if (onClose) onClose();
            }}
            className="btn-gold"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ivory p-8 md:p-10 max-w-2xl mx-auto relative">
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-text-muted hover:text-charcoal transition-colors"
        >
          <X size={24} />
        </button>
      )}
      
      <div className="text-center mb-10">
        <p className="eyebrow mb-4">Contacto</p>
        <h3 className="font-serif text-2xl text-charcoal mb-3">
          Solicitar valoración
        </h3>
        <p className="text-text-secondary text-sm">
          Complete el formulario y nos pondremos en contacto con usted para programar su consulta.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Nombre */}
        <div>
          <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
            Nombre completo *
          </label>
          <input
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleInputChange}
            required
            className="input-luxury w-full"
            placeholder="Su nombre completo"
          />
        </div>

        {/* Teléfono y Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
              Teléfono *
            </label>
            <input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleInputChange}
              required
              className="input-luxury w-full"
              placeholder="Su número de teléfono"
            />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
              Correo electrónico
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="input-luxury w-full"
              placeholder="su@email.com"
            />
          </div>
        </div>

        {/* Peso perdido y tiempo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
              Peso perdido aproximado (kg)
            </label>
            <input
              type="text"
              name="peso_perdido"
              value={formData.peso_perdido}
              onChange={handleInputChange}
              className="input-luxury w-full"
              placeholder="Ej: 40"
            />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
              Tiempo con peso estable
            </label>
            <select
              name="tiempo_estabilizado"
              value={formData.tiempo_estabilizado}
              onChange={handleInputChange}
              className="input-luxury w-full bg-ivory"
            >
              <option value="">Seleccione...</option>
              <option value="menos_6_meses">Menos de 6 meses</option>
              <option value="6_12_meses">6 - 12 meses</option>
              <option value="12_18_meses">12 - 18 meses</option>
              <option value="mas_18_meses">Más de 18 meses</option>
            </select>
          </div>
        </div>

        {/* Procedimientos de interés */}
        <div>
          <label className="block text-xs tracking-wider uppercase text-text-muted mb-3">
            Procedimientos de interés
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {procedimientosOpciones.map((proc) => (
              <label
                key={proc.id}
                className={`flex items-center gap-3 p-3 border cursor-pointer transition-all ${
                  formData.procedimientos_interes.includes(proc.id)
                    ? 'border-gold bg-gold/5'
                    : 'border-divider hover:border-gold/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.procedimientos_interes.includes(proc.id)}
                  onChange={() => handleProcedimientoToggle(proc.id)}
                  className="w-4 h-4 text-gold border-divider focus:ring-gold"
                />
                <span className="text-sm text-text-secondary">{proc.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Mensaje */}
        <div>
          <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
            Cuéntenos su caso
          </label>
          <textarea
            name="mensaje"
            value={formData.mensaje}
            onChange={handleInputChange}
            rows={4}
            className="input-luxury w-full resize-none"
            placeholder="Describa brevemente su situación, tipo de cirugía bariátrica realizada, zonas que le preocupan..."
          />
        </div>

        {/* Error message */}
        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
            Ha ocurrido un error al enviar el formulario. Por favor, inténtelo de nuevo o llámenos directamente al +34 952 76 45 47.
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-gold w-full disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Enviando...
            </>
          ) : (
            'Enviar solicitud'
          )}
        </button>

        <p className="text-xs text-text-muted text-center">
          Al enviar este formulario, acepta que sus datos sean tratados para gestionar su consulta. 
          Sus datos serán tratados de forma confidencial.
        </p>
      </form>
    </div>
  );
};

export default ContactFormPostbariatrica;
